import React from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, Circle, Clock, AlertCircle, Flag, Calendar, Trash2 } from 'lucide-react';
import { formatDate, getUserTimezone } from '../utils/dateUtils';

/**
 * TaskCard component - Displays a single task in a compact card format
 */
const TaskCard = ({ task, onTaskClick, onDelete }) => {
  const userTimezone = getUserTimezone();
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="text-green-600" size={20} />;
      case 'in_progress':
        return <Clock className="text-blue-600" size={20} />;
      case 'under_review':
        return <AlertCircle className="text-purple-600" size={20} />;
      case 'not_relevant':
        return <Circle className="text-gray-300" size={20} />;
      default:
        return <Circle className="text-gray-400" size={20} />;
    }
  };

  return (
    <div
      className="border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-blue-300 transition bg-white cursor-pointer group min-h-[44px]"
      onClick={() => onTaskClick(task)}
    >
      {/* Responsive Layout - Stack on mobile, row on desktop */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Status Icon and Title - Always in a row */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Status Icon */}
          <div className="flex-shrink-0">
            {getStatusIcon(task.status)}
          </div>

          {/* Task Title */}
          <div className="flex-1 min-w-0">
            <h3 className={`text-sm font-semibold truncate ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {task.title}
            </h3>
          </div>
        </div>

        {/* Icon Tags with Hover Tooltips - Wrap on mobile */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:flex-shrink-0">
          {/* Status Badge */}
          <div className="relative group/status">
            <div className={`w-2 h-2 rounded-full ${
              task.status === 'completed' ? 'bg-green-500' :
              task.status === 'in_progress' ? 'bg-blue-500' :
              task.status === 'under_review' ? 'bg-purple-500' :
              task.status === 'not_relevant' ? 'bg-gray-400' :
              'bg-red-500'
            }`}></div>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/status:opacity-100 transition pointer-events-none z-10">
              Status: {task.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
          </div>

          {/* Priority Flag */}
          <div className="relative group/priority">
            <Flag size={14} className={`${
              task.priority === 'high' ? 'text-red-600 fill-red-600' :
              task.priority === 'medium' ? 'text-yellow-600 fill-yellow-600' :
              'text-green-600 fill-green-600'
            }`} />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/priority:opacity-100 transition pointer-events-none z-10">
              Priority: {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
            </div>
          </div>

          {/* Due Date Calendar */}
          {task.due_date && (
            <div className="relative group/date">
              <Calendar size={14} className="text-indigo-600" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/date:opacity-100 transition pointer-events-none z-10">
                Due Date: {formatDate(task.due_date, userTimezone)}
              </div>
            </div>
          )}

          {/* Activity Type */}
          {task.question?.activity_type && (
            <div className="relative group/activity">
              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-[10px] font-bold text-emerald-700">A</span>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/activity:opacity-100 transition pointer-events-none z-10">
                Activity: {task.question.activity_type}
              </div>
            </div>
          )}

          {/* Intangible Capital */}
          {task.question?.intangible_asset_type && (
            <div className="relative group/capital">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-[10px] font-bold text-amber-700">I</span>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/capital:opacity-100 transition pointer-events-none z-10">
                Intangibles: {task.question.intangible_asset_type}
              </div>
            </div>
          )}

          {/* Category */}
          {task.question?.category_display && (
            <div className="relative group/category">
              <div className="w-6 h-6 rounded-full bg-fuchsia-100 flex items-center justify-center">
                <span className="text-[10px] font-bold text-fuchsia-700">C</span>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/category:opacity-100 transition pointer-events-none z-10">
                Category: {task.question.category_display}
              </div>
            </div>
          )}

          {/* Theme */}
          {task.question?.subject && (
            <div className="relative group/theme">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center">
                <span className="text-[10px] font-bold text-cyan-700">T</span>
              </div>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/theme:opacity-100 transition pointer-events-none z-10 max-w-xs">
                Theme: {task.question.subject}
              </div>
            </div>
          )}

          {/* Delete Button (only for non-system tasks) - Always visible on mobile */}
          {!task.is_system_task && onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition sm:opacity-0 sm:group-hover:opacity-100 min-w-[28px] min-h-[28px] flex items-center justify-center"
              title="Delete task"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

TaskCard.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string.isRequired,
    due_date: PropTypes.string,
    is_system_task: PropTypes.bool,
    question: PropTypes.shape({
      activity_type: PropTypes.string,
      intangible_asset_type: PropTypes.string,
      category_display: PropTypes.string,
      subject: PropTypes.string,
    }),
  }).isRequired,
  onTaskClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default TaskCard;
